/**
 * L1「画风」→ L2「配色」的自动派生(V0 定案 3.2.2)—— 消灭「手动搭建出图退纯色贴字」。
 *
 * ═══ 这修的是一个结构性缺陷,不是渲染 bug ═══════════════════════════════════════
 * `styleTokens` 是 ai 侧**双驱动的唯一视觉源**(AI 图块的 prompt DNA + PIL 矢量块的配色)。
 * 但改之前它只有 Canvas 智能规划那条路才有:手动搭建的 draft 根本不接 styleTokens
 * (类型层 `blocks.ts` 早就绪、`buildLongImagePayload` 也只是「有才序列化」,缺的就是 state),
 * 于是 ai 侧退回存量 `background` 三档推色 → **纯矢量贴字**。
 * 用户的原话是「我选了风格,出来还是很朴素」—— 那不是审美问题,是他走的那条路径没有 DNA。
 * 让「有没有 DNA」取决于用户走了哪条路,等于把实现细节漏给用户。
 *
 * ═══ ★ `source` 只能是 `"preset"`,不能是 `"derived"` ═════════════════════════
 * 设计方案 §3.2.2 原文写的是 `source:"derived"`,**那个值会 422**:
 * ai `app/modules/poster/longimage/style_tokens.py:21` 的
 * `TokenSource = Literal["auto","preset","manual"]` + `extra="forbid"`。
 * 「跟随画风」是**纯前端 state**(存 draft、不进 payload),推出来的 tokens 上送 `"preset"`。
 *
 * ═══ ★ 为什么只借已核准预设的 palette,不为 39 个画风各配一套色 ═════════════════
 * `styleTokenPresets` 里的 hex 是**产物颜色**(要发给引擎的调色板),不是界面色 ——
 * 新增一套就是一个**产品决定**,得有人拍板。所以这里严格照实施口径:
 * palette 从已核准预设里取最近的一个,**只微调 material / lighting / tone / density**
 * 这四个枚举(它们才是"这个画风该长什么质感"的表达)。
 * ★已知代价:水墨 / 国潮这几族没有专属色板,只能借最近的一套
 * (水墨借 3C 科技 —— 它的 ink 是近黑 `#0F172A`、bg 近白,是最接近墨色的)。
 *
 * ═══ ★ `base` 为什么是必填字段而不是从 tone 文本里认 ═══════════════════════════
 * 2026-07-30 补了 dark_tech / dark_cinema 两套深色板,把 4 个深色基底画风接过去。
 * 但真正的教训不是"借错了色",是**没有任何东西拦得住借错** —— tone 里写着
 * 「石墨黑深色基底」、色板给的是 `bg #F8FAFF`,两句一起发给引擎,谁也没红。
 * 所以判据不能靠读 tone 散文(「墨黑」出现在国潮里而国潮是米白宣纸底,一个关键词
 * 就能把闸判反),得让作者**显式声明**这个画风的基底是深是浅:
 *   `base` 是 TS 必填 → 加画风必须回答这个问题;
 *   `toneToTokens.test.ts` 断言「声明 dark ⇒ 借到的 palette.bg 亮度真的低」
 *   + ink/bg 对比度 —— 声明与色板对不上就红。
 * 声明可以写错,但写错会当场被闸抓住;不声明连抓的机会都没有。
 */
import type {
  Density,
  Lighting,
  Material,
  StyleTokens,
  StyleTone,
} from "../../longimage/model/canvas";
import { findStyleTokenPreset } from "../../longimage/model/styleTokenPresets";
import { fontPairForStyle } from "./fontPair";

/** 一个画风的映射:借哪套 palette + 这个画风该有的质感四件套。 */
interface ToneMapping {
  /** 这个画风的画面基底是深是浅 —— 必填,借到的 palette.bg 必须与它一致(见文件头)。 */
  base: "light" | "dark";
  /** 已核准预设之一 —— 只取它的 `palette`。 */
  preset: string;
  material: Material;
  lighting: Lighting;
  tone: StyleTone;
  density: Density;
}

/**
 * 39 个画风 → 配色映射。★必须与 `styleOptions.ts` 的 `PRESET_STYLES` 逐 key 对齐,
 * 漏一个 `toneToTokens.test.ts` 就红(它遍历 PRESET_STYLES 校验,不是校验这张表自己)。
 *
 * 分族理由写在每段头上 —— 下一个人要改某个画风时,先看它凭什么进这一族。
 */
const TONE_MAPPING: Record<string, ToneMapping> = {
  // ── 业务核心:冷色金属/玻璃族 ────────────────────────────────────────
  // 浅底的借 3C 科技深空蓝;数据大屏/科幻大片 tone 明写深色基底 → 走深色板
  "ai-future": {
    base: "light",
    preset: "tech_3c",
    material: "frosted",
    lighting: "studio",
    tone: "tech_cool",
    density: "airy",
  },
  dashboard: {
    base: "dark",
    preset: "dark_tech",
    material: "glossy",
    lighting: "studio",
    tone: "tech_cool",
    density: "compact",
  },
  scifi: {
    base: "dark",
    preset: "dark_cinema",
    material: "metallic",
    lighting: "dramatic",
    tone: "tech_cool",
    density: "compact",
  },
  // 企业商务/高级极简/真实摄影:同族但要"安静" —— 柔光 + 留白 + 高级感调性
  corporate: {
    base: "light",
    preset: "tech_3c",
    material: "frosted",
    lighting: "soft",
    tone: "premium_minimal",
    density: "airy",
  },
  minimal: {
    base: "light",
    preset: "tech_3c",
    material: "matte",
    lighting: "soft",
    tone: "premium_minimal",
    density: "airy",
  },
  photography: {
    base: "light",
    preset: "tech_3c",
    material: "natural",
    lighting: "natural",
    tone: "premium_minimal",
    density: "airy",
  },
  // 强转化广告/扁平插画:暖橙促销族
  "hard-sell": {
    base: "light",
    preset: "food_promo",
    material: "glossy",
    lighting: "dramatic",
    tone: "vivid_promo",
    density: "compact",
  },
  "flat-illustration": {
    base: "light",
    preset: "fresh_mom_baby",
    material: "matte",
    lighting: "soft",
    tone: "fresh_clean",
    density: "normal",
  },

  // ── 二次元 · 动画:按"干净治愈 / 热血冲击 / 柔和少女"三条线分 ─────────
  anime: {
    base: "light",
    preset: "fresh_mom_baby",
    material: "glossy",
    lighting: "soft",
    tone: "fresh_clean",
    density: "normal",
  },
  ghibli: {
    base: "light",
    preset: "home_warm",
    material: "natural",
    lighting: "natural",
    tone: "warm_life",
    density: "airy",
  },
  shonen: {
    base: "light",
    preset: "sport_outdoor",
    material: "glossy",
    lighting: "dramatic",
    tone: "vivid_promo",
    density: "compact",
  },
  shoujo: {
    base: "light",
    preset: "premium_beauty",
    material: "frosted",
    lighting: "soft",
    tone: "premium_minimal",
    density: "airy",
  },
  "cel-3d": {
    base: "light",
    preset: "fresh_mom_baby",
    material: "glossy",
    lighting: "studio",
    tone: "fresh_clean",
    density: "normal",
  },
  "thick-paint": {
    base: "light",
    preset: "home_warm",
    material: "natural",
    lighting: "dramatic",
    tone: "warm_life",
    density: "normal",
  },
  // 黑白漫画 / 剧情国漫:墨线为主 → 借 3C 的近黑 ink
  comic: {
    base: "light",
    preset: "tech_3c",
    material: "matte",
    lighting: "dramatic",
    tone: "tech_cool",
    density: "compact",
  },
  "manhua-drama": {
    base: "light",
    preset: "tech_3c",
    material: "matte",
    lighting: "dramatic",
    tone: "tech_cool",
    density: "compact",
  },
  "pixar-3d": {
    base: "light",
    preset: "fresh_mom_baby",
    material: "glossy",
    lighting: "studio",
    tone: "fresh_clean",
    density: "normal",
  },
  "3d-toy": {
    base: "light",
    preset: "food_promo",
    material: "glossy",
    lighting: "studio",
    tone: "vivid_promo",
    density: "normal",
  },
  "disney-2d": {
    base: "light",
    preset: "home_warm",
    material: "matte",
    lighting: "soft",
    tone: "warm_life",
    density: "normal",
  },
  "western-cartoon": {
    base: "light",
    preset: "food_promo",
    material: "matte",
    lighting: "natural",
    tone: "vivid_promo",
    density: "normal",
  },
  claymation: {
    base: "light",
    preset: "home_warm",
    material: "natural",
    lighting: "soft",
    tone: "warm_life",
    density: "normal",
  },
  mecha: {
    base: "dark",
    preset: "dark_tech",
    material: "metallic",
    lighting: "dramatic",
    tone: "tech_cool",
    density: "compact",
  },

  // ── 创意画风 ────────────────────────────────────────────────────────
  // 国潮 / 武侠:中国红 + 戏剧光(6 套里 sport_outdoor 的活力红最接近国潮红)
  guochao: {
    base: "light",
    preset: "sport_outdoor",
    material: "matte",
    lighting: "dramatic",
    tone: "vivid_promo",
    density: "normal",
  },
  wuxia: {
    base: "light",
    preset: "sport_outdoor",
    material: "natural",
    lighting: "dramatic",
    tone: "warm_life",
    density: "airy",
  },
  // 水墨:借 3C 的近黑 ink + 近白 bg,靠 matte/airy 压出宣纸留白感(见文件头已知代价)
  "ink-wash": {
    base: "light",
    preset: "tech_3c",
    material: "natural",
    lighting: "soft",
    tone: "premium_minimal",
    density: "airy",
  },
  // 赛博朋克「深靛蓝与石墨黑为基底」/ 机甲高达「冷灰基底」→ 深色板(2026-07-30)
  cyberpunk: {
    base: "dark",
    preset: "dark_cinema",
    material: "glossy",
    lighting: "dramatic",
    tone: "tech_cool",
    density: "compact",
  },
  vaporwave: {
    base: "light",
    preset: "premium_beauty",
    material: "glossy",
    lighting: "dramatic",
    tone: "vivid_promo",
    density: "normal",
  },
  steampunk: {
    base: "light",
    preset: "home_warm",
    material: "metallic",
    lighting: "dramatic",
    tone: "warm_life",
    density: "compact",
  },
  y2k: {
    base: "light",
    preset: "premium_beauty",
    material: "glossy",
    lighting: "studio",
    tone: "vivid_promo",
    density: "compact",
  },
  pixel: {
    base: "light",
    preset: "tech_3c",
    material: "matte",
    lighting: "studio",
    tone: "tech_cool",
    density: "compact",
  },
  surreal: {
    base: "light",
    preset: "premium_beauty",
    material: "frosted",
    lighting: "dramatic",
    tone: "premium_minimal",
    density: "airy",
  },

  // ── 更多画风:绘画媒介 / 复古 / 长尾 ─────────────────────────────────
  "hk-90s": {
    base: "light",
    preset: "food_promo",
    material: "glossy",
    lighting: "dramatic",
    tone: "vivid_promo",
    density: "compact",
  },
  collage: {
    base: "light",
    preset: "food_promo",
    material: "matte",
    lighting: "natural",
    tone: "vivid_promo",
    density: "compact",
  },
  linocut: {
    base: "light",
    preset: "tech_3c",
    material: "matte",
    lighting: "studio",
    tone: "tech_cool",
    density: "normal",
  },
  "oil-painting": {
    base: "light",
    preset: "home_warm",
    material: "natural",
    lighting: "dramatic",
    tone: "warm_life",
    density: "normal",
  },
  sketch: {
    base: "light",
    preset: "tech_3c",
    material: "matte",
    lighting: "soft",
    tone: "premium_minimal",
    density: "airy",
  },
  watercolor: {
    base: "light",
    preset: "home_warm",
    material: "natural",
    lighting: "soft",
    tone: "warm_life",
    density: "airy",
  },
  "maximalist-infographic": {
    base: "light",
    preset: "food_promo",
    material: "glossy",
    lighting: "studio",
    tone: "vivid_promo",
    density: "compact",
  },
  doodle: {
    base: "light",
    preset: "fresh_mom_baby",
    material: "matte",
    lighting: "natural",
    tone: "fresh_clean",
    density: "normal",
  },
};

/**
 * 画风 key → 一份完整 `StyleTokens`。
 *
 * @param styleKey `styleOptions.ts` 的 key;「自由发挥」无 key,自定义画风也无 key。
 * @param fontStyleKey 字体档(L2 的一部分,定案 3.2.1);缺省走预设默认。
 * @returns `null` = 这个画风推不出配色(自由发挥 / 未知 key)——
 *          调用方应保持现有配色不动,**不要**兜底成某个具体色板:
 *          「自由发挥」的意思就是让 AI 自己定,替它选一套等于把这个档位废掉。
 */
export function toneToTokens(
  styleKey: string | null | undefined,
  fontStyleKey?: string | null
): StyleTokens | null {
  if (!styleKey) return null;
  const mapping = TONE_MAPPING[styleKey];
  if (!mapping) return null;
  const preset = findStyleTokenPreset(mapping.preset);
  if (!preset) return null;
  return {
    // palette 整份借用(产物颜色,已核准;见文件头)
    palette: { ...preset.tokens.palette },
    material: mapping.material,
    lighting: mapping.lighting,
    tone: mapping.tone,
    density: mapping.density,
    fontPair: fontPairForStyle(fontStyleKey),
    // ★ 只能是 preset —— derived 会被 ai 侧 extra="forbid" 打成 422(见文件头)
    source: "preset",
  };
}

/** 这个画风能不能推出配色(UI 决定要不要显示「跟随画风」时用)。 */
export function canDeriveTokens(styleKey: string | null | undefined): boolean {
  return !!styleKey && styleKey in TONE_MAPPING;
}

/**
 * 这个画风**声明**的画面基底(深/浅)。
 *
 * ★只给闸用,不给业务用:它存在的意义是让 `toneToTokens.test.ts` 能拿声明去对
 * 借到的 `palette.bg`,把「tone 说深色底、色板给近白底」这类对撞挡在提交之前。
 * 业务侧要判深浅请直接读 `palette.bg` 的亮度 —— 那才是真正发给引擎的东西,
 * 而声明只是作者的意图(意图与产物不一致时,红的应该是闸,不是用户的图)。
 */
export function styleBaseOf(styleKey: string | null | undefined): "light" | "dark" | null {
  if (!styleKey) return null;
  return TONE_MAPPING[styleKey]?.base ?? null;
}
